import DeleteIcon from '@/assets/icons/fs-delete.svg?react'
import DownloadIcon from '@/assets/icons/fs-download.svg?react'
import MoveIcon from '@/assets/icons/fs-move.svg?react'
import RenameIcon from '@/assets/icons/fs-rename.svg?react'
import { Trans } from '@/components'
import { cn } from '@/utils/style'

import { IFile } from '../core/types'

interface BulkActionBarProps {
  selectedFiles: IFile[]
  onDownload: () => void
  onMove: () => void
  onRename: () => void
  onDelete: () => void
  disableEdit?: boolean
}

export function BulkActionBar({
  selectedFiles,
  onDownload,
  onMove,
  onRename,
  onDelete,
  disableEdit = false,
}: BulkActionBarProps) {
  if (selectedFiles.length === 0) return null

  const hasFolder = selectedFiles.some((file) => file.isDir)
  const isMultipleSelection = selectedFiles.length > 1

  return (
    <div className="fixed bottom-6 left-[272px] right-4 z-50 flex justify-center pointer-events-none">
      <div
        className={cn(
          'pointer-events-auto flex gap-2 items-center bg-panel-white border border-neutral-100 rounded-xl p-2 shadow-card-100',
        )}
      >
        {/* Download button - disabled when folder selected */}
        <button
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-primary hover:bg-panel-gray-100',
            hasFolder && 'opacity-50 cursor-not-allowed',
          )}
          onClick={onDownload}
          disabled={hasFolder}
        >
          <DownloadIcon className="w-5 h-5" />
          <span>
            <Trans i18nKey="data.bulkAction.download">Download</Trans>
          </span>
        </button>

        {/* Move button - disabled when disableEdit */}
        <button
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-primary hover:bg-panel-gray-100',
            disableEdit && 'opacity-50 cursor-not-allowed',
          )}
          onClick={onMove}
          disabled={disableEdit}
        >
          <MoveIcon className="w-5 h-5" />
          <span>
            <Trans i18nKey="data.bulkAction.move">Move</Trans>
          </span>
        </button>

        {/* Rename button - disabled when multiple files selected or disableEdit */}
        <button
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-primary hover:bg-panel-gray-100',
            (isMultipleSelection || disableEdit) && 'opacity-50 cursor-not-allowed',
          )}
          onClick={onRename}
          disabled={isMultipleSelection || disableEdit}
        >
          <RenameIcon className="w-5 h-5" />
          <span>
            <Trans i18nKey="data.bulkAction.rename">Rename</Trans>
          </span>
        </button>

        {/* Delete button - disabled when disableEdit */}
        <button
          className={cn(
            'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-error hover:bg-error/10',
            disableEdit && 'opacity-50 cursor-not-allowed',
          )}
          onClick={onDelete}
          disabled={disableEdit}
        >
          <DeleteIcon className="w-5 h-5" />
          <span>
            <Trans i18nKey="data.bulkAction.delete">Delete</Trans>
          </span>
        </button>
      </div>
    </div>
  )
}
